export type QuoteGuidanceRule = {
  rule_id: string;
  label: string;
  operator: "equals" | "contains" | "gte" | "lte" | "between";
  comparison_value: string | number | boolean;
  comparison_value_max?: number | null;
  unit_price_delta_ex_vat?: number | null;
  unit_price_multiplier?: number | null;
  labor_hours_per_output_unit?: number | null;
  labor_time_multiplier?: number | null;
  fixed_cost_delta_ex_vat?: number | null;
  inventory_item_id?: string | null;
  inventory_application_passes?: number | null;
  inventory_billing_mode?: "included" | "separate" | null;
  description_line?: string | null;
  line_item_mode?: "catalog" | "client" | "one_off" | null;
  line_item_catalog_id?: string | null;
  line_item_name?: string | null;
  line_item_quantity?: number | null;
  line_item_unit_price_ex_vat?: number | null;
  line_item_vat_rate?: number | null;
};

export type QuoteGuidanceQuestion = {
  question_key: string;
  label: string;
  response_type:
    | "boolean"
    | "number"
    | "integer"
    | "single_choice"
    | "multiple_choice"
    | "text";
  required: boolean;
  unit_label: string | null;
  options: Array<{ value: string; label: string }>;
  costing_target:
    | "output_quantity"
    | "total_labor_hours"
    | "team_size"
    | "travel_cost_ex_vat"
    | "other_cost_ex_vat"
    | "contingency_rate"
    | null;
  pricing_rules: QuoteGuidanceRule[];
};

export type QuoteGuidanceMode =
  | "composition"
  | "parametric"
  | "hybrid"
  | "informational";

export function detectQuoteGuidanceMode(
  questions: QuoteGuidanceQuestion[],
): QuoteGuidanceMode {
  const rules = questions.flatMap((question) => question.pricing_rules || []),
    hasComposition = rules.some((rule) => Boolean(rule.line_item_mode)),
    hasParametric = rules.some(
      (rule) =>
        rule.unit_price_delta_ex_vat != null ||
        (rule.unit_price_multiplier != null && rule.unit_price_multiplier !== 1) ||
        rule.labor_hours_per_output_unit != null ||
        (rule.labor_time_multiplier != null && rule.labor_time_multiplier !== 1) ||
        (rule.fixed_cost_delta_ex_vat != null && rule.fixed_cost_delta_ex_vat !== 0) ||
        Boolean(rule.inventory_item_id),
    );
  if (hasComposition && hasParametric) return "hybrid";
  if (hasComposition) return "composition";
  if (hasParametric) return "parametric";
  return "informational";
}

export type GuidanceEvidencePricingProposal = {
  key: "minimum" | "advised" | "comfortable";
  label: string;
  marginRate: number;
  unitPriceExVat: number;
};

export function buildGuidanceEvidencePricingProposals(input: {
  evidenceCount: number;
  averageCostExVatPerUnit: number | null | undefined;
  targetMarginRate: number | null | undefined;
}): GuidanceEvidencePricingProposal[] {
  const cost = Number(input.averageCostExVatPerUnit),
    minimumMargin = Math.min(85, Math.max(0, Number(input.targetMarginRate ?? 40)));
  if (
    input.evidenceCount < 3 ||
    !Number.isFinite(cost) ||
    cost <= 0
  )
    return [];
  const proposals: Array<
    Omit<GuidanceEvidencePricingProposal, "unitPriceExVat">
  > = [
    { key: "minimum", label: "Minimum protégé", marginRate: minimumMargin },
    {
      key: "advised",
      label: "Conseillé",
      marginRate: Math.min(90, minimumMargin + 5),
    },
    {
      key: "comfortable",
      label: "Confortable",
      marginRate: Math.min(90, minimumMargin + 10),
    },
  ];
  return proposals.map((proposal) => ({
    ...proposal,
    unitPriceExVat: round(cost / (1 - proposal.marginRate / 100)),
  }));
}

export type QuoteGuidanceCatalogItem = {
  id: string;
  name: string;
  item_kind: "service" | "product" | "rental" | "other";
  default_billing_mode: string;
  default_unit_label: string;
  default_unit_price_ex_vat: number;
  default_vat_rate?: number;
  target_margin_rate: number | null;
};

export type QuoteGuidanceLineItem = {
  sourceRuleId: string;
  sourceRuleLabel: string;
  mode: "catalog" | "client" | "one_off";
  catalogItemId: string | null;
  name: string;
  quantity: number;
  unitPriceExVat: number;
  vatRate: number;
  totalExVat: number;
};

export type QuoteGuidanceCostingProfile = {
  id: string;
  output_unit: string;
  expected_output_per_labor_hour: number | null;
  default_team_size: number;
  default_labor_cost_per_hour: number | null;
  default_travel_cost_ex_vat: number;
  contingency_rate: number;
  target_mode: "margin" | "markup";
  target_rate: number;
  confidence: string;
};

export type QuoteGuidanceCostComponent = {
  id: string;
  label: string;
  component_type: "equipment" | "other";
  cost_basis: "per_job" | "per_elapsed_hour" | "per_output_unit";
  quantity: number;
  unit_cost_ex_vat: number;
};

export type QuoteGuidanceInventoryItem = {
  id: string;
  name: string;
  purchase_package_quantity?: number | null;
  purchase_package_unit?: "L" | "mL" | "kg" | "g" | "unite" | null;
  purchase_package_price_ex_vat?: number | null;
  yield_material_quantity?: number | null;
  yield_material_unit?: "L" | "mL" | "kg" | "g" | "unite" | null;
  yield_output_quantity?: number | null;
  yield_output_unit?: "m2" | "m3" | "metre_lineaire" | "unite" | null;
  default_application_passes?: number | null;
  loss_rate_percent?: number | null;
  target_margin_rate?: number | null;
};

export type QuoteGuidanceMaterialEstimate = {
  inventoryItemId: string;
  inventoryItemName: string;
  materialQuantity: number;
  materialUnit: string;
  estimatedCostExVat: number;
  suggestedSalePriceExVat: number;
  billingMode: "included" | "separate";
};

export type QuoteGuidanceEvaluation = {
  missingRequired: string[];
  validationErrors: string[];
  appliedRules: Array<{
    questionKey: string;
    questionLabel: string;
    ruleId: string;
    ruleLabel: string;
    answer: unknown;
  }>;
  derivedInputs: Record<string, unknown>;
  outputQuantity: number | null;
  outputUnit: string | null;
  totalLaborHours: number | null;
  commercialRulePriceExVat: number | null;
  totalCostExVat: number | null;
  costBasedMinimumExVat: number | null;
  recommendedTotalExVat: number | null;
  missingCostInputs: string[];
  materialEstimates: QuoteGuidanceMaterialEstimate[];
  lineItems: QuoteGuidanceLineItem[];
  descriptionLines: string[];
  canPrepareDraft: boolean;
  requiresCostReview: boolean;
};

function emptyAnswer(answer: unknown) {
  return (
    answer == null ||
    answer === "" ||
    (Array.isArray(answer) && answer.length === 0)
  );
}

const materialUnits: Record<string, { family: string; factor: number }> = {
  L: { family: "volume", factor: 1 },
  mL: { family: "volume", factor: 0.001 },
  kg: { family: "mass", factor: 1 },
  g: { family: "mass", factor: 0.001 },
  unite: { family: "count", factor: 1 },
};

function interpolateDescription(
  template: string,
  quantity: number | null,
  unit: string | null,
) {
  return template
    .replaceAll("{quantite}", quantity == null ? "quantité à confirmer" : String(quantity))
    .replaceAll("{surface}", quantity == null ? "surface à confirmer" : String(quantity))
    .replaceAll("{unite}", unit || "unité");
}

function ruleMatches(answer: unknown, rule: QuoteGuidanceRule) {
  const numericAnswer = Number(answer),
    numericComparison = Number(rule.comparison_value);
  switch (rule.operator) {
    case "equals":
      return answer === rule.comparison_value;
    case "contains":
      return Array.isArray(answer) && answer.includes(rule.comparison_value);
    case "gte":
      return Number.isFinite(numericAnswer) && numericAnswer >= numericComparison;
    case "lte":
      return Number.isFinite(numericAnswer) && numericAnswer <= numericComparison;
    case "between":
      return (
        Number.isFinite(numericAnswer) &&
        numericAnswer >= numericComparison &&
        numericAnswer <= Number(rule.comparison_value_max)
      );
    default:
      return false;
  }
}

function round(value: number, precision = 2) {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

export function evaluateQuoteGuidance(input: {
  catalogItem: QuoteGuidanceCatalogItem;
  questions: QuoteGuidanceQuestion[];
  answers: Record<string, unknown>;
  costingProfile?: QuoteGuidanceCostingProfile | null;
  costComponents?: QuoteGuidanceCostComponent[];
  inventoryItems?: QuoteGuidanceInventoryItem[];
  catalogItems?: QuoteGuidanceCatalogItem[];
  catalogPriceOverrides?: Record<string, number>;
  catalogVatOverrides?: Record<string, number>;
  clientServiceOverrides?: Record<
    string,
    { unitPriceExVat: number; vatRate: number }
  >;
  defaultTargetMode?: "margin" | "markup";
  defaultTargetRate?: number;
}): QuoteGuidanceEvaluation {
  const guidanceMode = detectQuoteGuidanceMode(input.questions);
  const missingRequired: string[] = [],
    validationErrors: string[] = [],
    appliedRules: QuoteGuidanceEvaluation["appliedRules"] = [],
    derivedInputs: Record<string, unknown> = {},
    pendingMaterials: Array<{
      inventoryItemId: string;
      passes: number;
      billingMode: "included" | "separate";
    }> = [],
    pendingDescriptionLines: string[] = [];
  const lineItems: QuoteGuidanceLineItem[] = [];
  let unitPriceDelta = 0,
    unitPriceMultiplier = 1,
    laborTimeMultiplier = 1,
    laborHoursPerOutputUnit: number | null = null,
    fixedCostDelta = 0;

  for (const question of input.questions) {
    const answer = input.answers[question.question_key];
    if (emptyAnswer(answer)) {
      if (question.required) missingRequired.push(question.question_key);
      continue;
    }
    const numeric = Number(answer),
      valid =
        question.response_type === "boolean"
          ? typeof answer === "boolean"
          : question.response_type === "number"
            ? typeof answer === "number" && Number.isFinite(answer)
            : question.response_type === "integer"
              ? typeof answer === "number" && Number.isInteger(answer)
              : question.response_type === "text"
                ? typeof answer === "string"
                : question.response_type === "single_choice"
                  ? typeof answer === "string" &&
                    question.options.some((option) => option.value === answer)
                  : Array.isArray(answer) &&
                    answer.every((value) =>
                      question.options.some((option) => option.value === value),
                    );
    if (!valid) {
      validationErrors.push(`Réponse invalide : ${question.label}.`);
      continue;
    }
    if (
      ["number", "integer"].includes(question.response_type) &&
      numeric < 0
    )
      validationErrors.push(`${question.label} ne peut pas être négatif.`);
    if (
      question.costing_target &&
      ["output_quantity", "team_size"].includes(question.costing_target) &&
      numeric <= 0
    )
      validationErrors.push(`${question.label} doit être strictement positif.`);
    if (question.costing_target === "team_size" && numeric > 50)
      validationErrors.push("Une équipe ne peut pas dépasser 50 personnes.");
    if (question.costing_target === "contingency_rate" && numeric > 100)
      validationErrors.push("Le taux d’imprévu ne peut pas dépasser 100 %.");
    if (question.costing_target && Number.isFinite(numeric)) {
      derivedInputs[question.costing_target] = numeric;
      if (question.costing_target === "output_quantity" && question.unit_label)
        derivedInputs.output_unit = question.unit_label;
    }
    for (const rule of question.pricing_rules || []) {
      if (!ruleMatches(answer, rule)) continue;
      unitPriceDelta += Number(rule.unit_price_delta_ex_vat || 0);
      unitPriceMultiplier *= Number(rule.unit_price_multiplier ?? 1);
      laborTimeMultiplier *= Number(rule.labor_time_multiplier ?? 1);
      fixedCostDelta += Number(rule.fixed_cost_delta_ex_vat || 0);
      if (rule.inventory_item_id)
        pendingMaterials.push({
          inventoryItemId: rule.inventory_item_id,
          passes: Math.max(0.001, Number(rule.inventory_application_passes || 1)),
          billingMode:
            rule.inventory_billing_mode === "separate" ? "separate" : "included",
        });
      if (rule.description_line?.trim())
        pendingDescriptionLines.push(rule.description_line.trim());
      if (rule.line_item_mode) {
        const linkedCatalogItem = rule.line_item_catalog_id
            ? (input.catalogItems || []).find(
                (item) => item.id === rule.line_item_catalog_id,
              )
            : null,
          quantity = Number(rule.line_item_quantity ?? 1),
          clientOverride =
            rule.line_item_mode === "client" && rule.line_item_name
              ? input.clientServiceOverrides?.[
                  rule.line_item_name.trim().toLocaleLowerCase("fr")
                ]
              : null,
          unitPrice =
            rule.line_item_mode === "catalog" && linkedCatalogItem
              ? Number(
                  input.catalogPriceOverrides?.[linkedCatalogItem.id] ??
                    linkedCatalogItem.default_unit_price_ex_vat,
                )
              : Number(
                  clientOverride?.unitPriceExVat ??
                    rule.line_item_unit_price_ex_vat,
                ),
          vatRate =
            rule.line_item_mode === "catalog" && linkedCatalogItem
              ? Number(
                  input.catalogVatOverrides?.[linkedCatalogItem.id] ??
                    linkedCatalogItem.default_vat_rate ??
                    20,
                )
              : Number(
                  clientOverride?.vatRate ?? rule.line_item_vat_rate ?? 20,
                ),
          name =
            rule.line_item_mode === "catalog"
              ? linkedCatalogItem?.name || ""
              : rule.line_item_name?.trim() || "";
        if (
          (rule.line_item_mode === "catalog" && !linkedCatalogItem) ||
          !name ||
          !Number.isFinite(quantity) ||
          quantity <= 0 ||
          !Number.isFinite(unitPrice) ||
          unitPrice < 0 ||
          !Number.isFinite(vatRate) ||
          vatRate < 0 ||
          vatRate > 100
        ) {
          validationErrors.push(
            `La prestation déclenchée par « ${rule.label} » est incomplète.`,
          );
        } else {
          lineItems.push({
            sourceRuleId: rule.rule_id,
            sourceRuleLabel: rule.label,
            mode: rule.line_item_mode,
            catalogItemId: linkedCatalogItem?.id || null,
            name,
            quantity,
            unitPriceExVat: round(unitPrice),
            vatRate: round(vatRate),
            totalExVat: round(quantity * unitPrice),
          });
        }
      }
      if (rule.labor_hours_per_output_unit != null) {
        const next = Number(rule.labor_hours_per_output_unit);
        if (
          laborHoursPerOutputUnit != null &&
          laborHoursPerOutputUnit !== next
        )
          validationErrors.push(
            "Plusieurs règles fixent un temps absolu différent par unité.",
          );
        else laborHoursPerOutputUnit = next;
      }
      appliedRules.push({
        questionKey: question.question_key,
        questionLabel: question.label,
        ruleId: rule.rule_id,
        ruleLabel: rule.label,
        answer,
      });
    }
  }

  const outputQuantity =
      derivedInputs.output_quantity == null
        ? null
        : Number(derivedInputs.output_quantity),
    outputUnit =
      typeof derivedInputs.output_unit === "string"
        ? derivedInputs.output_unit
        : input.costingProfile?.output_unit || null;
  let totalLaborHours =
    derivedInputs.total_labor_hours == null
      ? null
      : Number(derivedInputs.total_labor_hours);
  if (totalLaborHours == null && outputQuantity != null) {
    if (laborHoursPerOutputUnit != null)
      totalLaborHours = outputQuantity * laborHoursPerOutputUnit;
    else if (
      input.costingProfile?.expected_output_per_labor_hour != null &&
      input.costingProfile.expected_output_per_labor_hour > 0
    )
      totalLaborHours =
        outputQuantity / input.costingProfile.expected_output_per_labor_hour;
  }
  if (totalLaborHours != null) {
    totalLaborHours = round(totalLaborHours * laborTimeMultiplier, 3);
    derivedInputs.total_labor_hours = totalLaborHours;
  }
  const materialEstimates: QuoteGuidanceMaterialEstimate[] = [],
    materialMissing: string[] = [];
  let materialCostExVat = 0,
    separatelyBilledMaterialsExVat = 0,
    lineItemsTotalExVat = 0;
  for (const impact of pendingMaterials) {
    const item = (input.inventoryItems || []).find(
        (candidate) => candidate.id === impact.inventoryItemId,
      ),
      packageUnit = materialUnits[item?.purchase_package_unit || ""],
      yieldUnit = materialUnits[item?.yield_material_unit || ""],
      packageQuantity = Number(item?.purchase_package_quantity),
      packagePrice = Number(item?.purchase_package_price_ex_vat),
      yieldMaterialQuantity = Number(item?.yield_material_quantity),
      yieldOutputQuantity = Number(item?.yield_output_quantity),
      lossRate = Number(item?.loss_rate_percent || 0);
    if (
      !item ||
      outputQuantity == null ||
      outputQuantity <= 0 ||
      !packageUnit ||
      !yieldUnit ||
      packageUnit.family !== yieldUnit.family ||
      !packageQuantity ||
      !Number.isFinite(packagePrice) ||
      !yieldMaterialQuantity ||
      !yieldOutputQuantity ||
      item.yield_output_unit !== outputUnit
    ) {
      materialMissing.push(`inventory:${impact.inventoryItemId}`);
      continue;
    }
    const packageBaseQuantity = packageQuantity * packageUnit.factor,
      materialBaseQuantity =
        (outputQuantity / yieldOutputQuantity) *
        yieldMaterialQuantity *
        yieldUnit.factor *
        impact.passes *
        (1 + Math.max(0, lossRate) / 100),
      materialQuantity = materialBaseQuantity / yieldUnit.factor,
      materialCost = (materialBaseQuantity / packageBaseQuantity) * packagePrice,
      marginRate = Number(
        item.target_margin_rate ??
          input.catalogItem.target_margin_rate ??
          input.defaultTargetRate ??
          40,
      ),
      suggestedSalePrice =
        marginRate >= 0 && marginRate < 100
          ? materialCost / (1 - marginRate / 100)
          : materialCost;
    materialCostExVat += materialCost;
    if (impact.billingMode === "separate")
      separatelyBilledMaterialsExVat += suggestedSalePrice;
    materialEstimates.push({
      inventoryItemId: item.id,
      inventoryItemName: item.name,
      materialQuantity: round(materialQuantity, 3),
      materialUnit: item.yield_material_unit || item.purchase_package_unit || "unite",
      estimatedCostExVat: round(materialCost),
      suggestedSalePriceExVat: round(suggestedSalePrice),
      billingMode: impact.billingMode,
    });
  }
  lineItemsTotalExVat = lineItems.reduce(
    (total, item) => total + item.totalExVat,
    0,
  );
  const descriptionLines = pendingDescriptionLines.map((line) =>
    interpolateDescription(line, outputQuantity, outputUnit),
  );
  Object.assign(derivedInputs, {
    guided_pricing_strategy: guidanceMode,
    pricing_basis: input.catalogItem.default_billing_mode,
    pricing_unit_label: input.catalogItem.default_unit_label,
    base_unit_price_ex_vat: Number(input.catalogItem.default_unit_price_ex_vat),
    unit_price_delta_ex_vat: unitPriceDelta,
    unit_price_multiplier: unitPriceMultiplier,
    fixed_cost_delta_ex_vat: fixedCostDelta,
    labor_hours_per_output_unit: laborHoursPerOutputUnit,
    labor_time_multiplier: laborTimeMultiplier,
    manual_price_validation_required: true,
    material_cost_ex_vat: round(materialCostExVat),
    material_estimates: materialEstimates,
    guided_line_items: lineItems,
    description_lines: descriptionLines,
  });
  const recommendedUnit = Math.max(
      0,
      (Number(input.catalogItem.default_unit_price_ex_vat || 0) +
        unitPriceDelta) *
        unitPriceMultiplier,
    ),
    pricingQuantity =
      input.catalogItem.default_billing_mode === "per_unit"
        ? outputQuantity
        : 1,
    commercialRulePriceExVat =
      pricingQuantity == null
        ? null
        : round(
            pricingQuantity * recommendedUnit +
              fixedCostDelta +
              separatelyBilledMaterialsExVat +
              lineItemsTotalExVat,
          );
  Object.assign(derivedInputs, {
    recommended_unit_price_ex_vat: round(recommendedUnit, 6),
    recommended_total_sale_price_ex_vat: commercialRulePriceExVat,
    applied_pricing_rules: appliedRules,
  });

  const profile = input.costingProfile,
    missingCostInputs: string[] = [],
    teamSize = Number(derivedInputs.team_size ?? profile?.default_team_size ?? 1),
    laborRate = profile?.default_labor_cost_per_hour,
    elapsedHours = totalLaborHours == null ? null : totalLaborHours / teamSize;
  if (input.catalogItem.item_kind === "service" && totalLaborHours == null)
    missingCostInputs.push("total_labor_hours");
  if (input.catalogItem.item_kind === "service" && laborRate == null)
    missingCostInputs.push("labor_cost_per_hour");
  let equipmentCost = 0,
    otherComponentCost = 0;
  for (const component of input.costComponents || []) {
    let amount: number | null = null;
    if (component.cost_basis === "per_job")
      amount = component.quantity * component.unit_cost_ex_vat;
    else if (component.cost_basis === "per_elapsed_hour" && elapsedHours != null)
      amount = component.quantity * component.unit_cost_ex_vat * elapsedHours;
    else if (component.cost_basis === "per_output_unit" && outputQuantity != null)
      amount =
        component.quantity * component.unit_cost_ex_vat * outputQuantity;
    if (amount == null) {
      missingCostInputs.push(
        `${component.cost_basis === "per_elapsed_hour" ? "elapsed_hours" : "output_quantity"}:${component.id}`,
      );
      continue;
    }
    if (component.component_type === "equipment") equipmentCost += amount;
    else otherComponentCost += amount;
  }
  const laborCost =
      totalLaborHours == null || laborRate == null
        ? 0
        : totalLaborHours * laborRate,
    travelCost = Number(
      derivedInputs.travel_cost_ex_vat ?? profile?.default_travel_cost_ex_vat ?? 0,
    ),
    otherCost =
      Number(derivedInputs.other_cost_ex_vat || 0) +
      otherComponentCost +
      materialCostExVat,
    contingencyRate = Number(
      derivedInputs.contingency_rate ?? profile?.contingency_rate ?? 10,
    ),
    costBeforeContingency =
      laborCost + equipmentCost + travelCost + otherCost,
    totalCostExVat =
      missingCostInputs.length > 0 || materialMissing.length > 0
        ? null
        : round(
            costBeforeContingency * (1 + Math.max(0, contingencyRate) / 100),
          ),
    targetMode =
      profile?.target_mode || input.defaultTargetMode || "margin",
    targetRate = Number(
      profile?.target_rate ??
        input.catalogItem.target_margin_rate ??
        input.defaultTargetRate ??
        40,
    ),
    costBasedMinimumExVat =
      totalCostExVat == null
        ? null
        : round(
            targetMode === "margin"
              ? totalCostExVat / (1 - targetRate / 100)
              : totalCostExVat * (1 + targetRate / 100),
          ),
    candidates = [commercialRulePriceExVat, costBasedMinimumExVat].filter(
      (value): value is number => value != null && Number.isFinite(value),
    ),
    recommendedTotalExVat = candidates.length ? Math.max(...candidates) : null;
  return {
    missingRequired,
    validationErrors: [...new Set(validationErrors)],
    appliedRules,
    derivedInputs,
    outputQuantity,
    outputUnit,
    totalLaborHours,
    commercialRulePriceExVat,
    totalCostExVat,
    costBasedMinimumExVat,
    recommendedTotalExVat,
    missingCostInputs: [...new Set([...missingCostInputs, ...materialMissing])],
    materialEstimates,
    lineItems,
    descriptionLines,
    canPrepareDraft:
      missingRequired.length === 0 &&
      validationErrors.length === 0 &&
      recommendedTotalExVat != null,
    requiresCostReview:
      missingCostInputs.length > 0 || materialMissing.length > 0,
  };
}
